import { useParams, Link } from 'react-router-dom';
import { 
  Users, Percent, IndianRupee, Calendar, Building2,
  Landmark, TrendingUp, Scale, Boxes, Ship, Factory, MapPin,
  FileText, Target, Clock, Shield, Award, CheckCircle2
} from 'lucide-react';
import { SubsidyHero } from '../components/sections/SubsidyHero';
import { SubsidyHighlights } from '../components/sections/SubsidyHighlights';
import { SubsidyOverview } from '../components/sections/SubsidyOverview';
import { SubsidyEligibility } from '../components/sections/SubsidyEligibility';
import { SubsidyBenefits } from '../components/sections/SubsidyBenefits';
import { SubsidyExtras } from '../components/sections/SubsidyExtras';
import { SubsidySchemeCTA } from '../components/sections/SubsidySchemeCTA';
import { SubsidyNavigation } from '../components/sections/SubsidyNavigation';

// Subsidy data
const subsidyData = {
  'rips-2024': {
    name: 'RIPS 2024',
    fullName: 'Rajasthan Investment Promotion Scheme 2024',
    icon: <Landmark className="w-10 h-10" />,
    color: 'from-blue-500 to-blue-600',
    validTill: 'March 31, 2029',
    tagline: 'Comprehensive investment incentives for manufacturing, services, and MSMEs',
    overview: 'The Rajasthan Investment Promotion Scheme (RIPS) 2024 offers tiered incentives including capital subsidies, investment subsidies (75% SGST reimbursement), interest subsidies, stamp duty exemptions, and sector-specific benefits to promote manufacturing, services, MSMEs, tourism, green projects, and agri-warehousing.',
    highlights: [
      { icon: <Percent />, title: 'Capital Subsidy', value: '25% of EFCI' },
      { icon: <IndianRupee />, title: 'Investment Subsidy', value: '75% of SGST' },
      { icon: <TrendingUp />, title: 'Interest Subsidy', value: 'Up to 7%' },
      { icon: <Calendar />, title: 'Duration', value: '7-10 Years' }
    ],
    eligibility: [
      'New or existing enterprises (expansions qualify if criteria met)',
      'Registered entities: Pvt. Ltd., partnerships, proprietorships',
      'Minimum EFCI varies by sector (₹25 Cr for MSMEs, ₹50 Cr manufacturing)',
      'Special focus: Women-led, rural, green, export, MSMEs, renewable energy',
      'Application via iStart portal with business plan'
    ],
    ineligible: [
      'Tobacco/pan masala manufacturing',
      'Cow beef processing',
      'Retail/trading activities',
      'City gas distribution',
      'Activities prohibited by government'
    ],
    benefits: [
      {
        category: 'Capital Subsidy',
        items: [
          '25% of eligible fixed capital investment (EFCI)',
          'Annual caps: Years 1-3: ₹10 Cr; Years 4-7: ₹15 Cr; Years 8-10: ₹20 Cr',
          'Higher limits for large manufacturing and mega projects'
        ]
      },
      {
        category: 'Investment Subsidy',
        items: [
          '75% of state taxes (SGST) for 7-10 years',
          'Capped at ₹50 Cr for Years 1-3 (large manufacturing)',
          '₹10 Cr cap for services sector'
        ]
      },
      {
        category: 'Interest Subsidy',
        items: [
          '6% for loans up to ₹5 Cr',
          '4% for loans ₹5-10 Cr',
          '3% for loans above ₹10 Cr',
          'Up to 7 years for MSMEs',
          'Additional 1% for rural tourism/agro units under ₹25 Lakh'
        ]
      },
      {
        category: 'MSME Specific',
        items: [
          'Total incentives capped at ₹5 Cr per annum',
          '50% capital subsidy for plastic alternatives (max ₹40 Lakh)',
          '50% for agro/food processing (max ₹1.5 Cr)',
          '+5% for ST/SC/women-led FPOs or tribal areas'
        ]
      },
      {
        category: 'Other Benefits',
        items: [
          '100% Electricity Duty Exemption for 7 years',
          '75% Stamp Duty Exemption + 25% Reimbursement',
          '50% Green/Asset Creation Incentives (max ₹10 Cr)',
          '100% Mandi Fee Refund for Agri-Warehousing'
        ]
      }
    ],
    projectCategories: [
      { category: 'MSME', investment: '₹25 Cr EFCI', keyIncentive: 'Total cap ₹5 Cr/year' },
      { category: 'Large', investment: '₹50 Cr / ₹25 Cr', keyIncentive: '75% SGST for 7 years' },
      { category: 'Mega', investment: '₹300 Cr / ₹100 Cr', keyIncentive: 'Higher caps/scaled benefits' },
      { category: 'Tourism', investment: '₹10 Cr', keyIncentive: 'Capital subsidy as above' }
    ]
  },
  'pmegp': {
    name: 'PMEGP',
    fullName: 'Prime Minister Employment Generation Programme',
    icon: <Users className="w-10 h-10" />,
    color: 'from-green-500 to-green-600',
    validTill: '2025-26',
    tagline: 'Self-employment through new micro-enterprises in non-farm sectors',
    overview: 'PMEGP is a central sector credit-linked subsidy scheme by the Ministry of MSME, aimed at generating self-employment through new micro-enterprises in manufacturing and service sectors. The scheme provides margin money (non-repayable subsidy) up to 35% of project cost.',
    highlights: [
      { icon: <Percent />, title: 'Max Subsidy', value: 'Up to 35%' },
      { icon: <IndianRupee />, title: 'Manufacturing', value: '₹50 Lakh' },
      { icon: <Building2 />, title: 'Service/Business', value: '₹20 Lakh' },
      { icon: <Users />, title: 'Beneficiary', value: '5-10% Contribution' }
    ],
    eligibility: [
      'Individuals above 18 years of age',
      'Self Help Groups, Charitable Trusts, Societies, Cooperatives',
      'VIII std. pass for manufacturing >₹10 lakh or service >₹5 lakh',
      'New units only (existing units not eligible)',
      'No income ceiling applies'
    ],
    ineligible: [
      'Existing units or those with prior government subsidy',
      'Meat/fish processing units',
      'Liquor/alcohol related activities',
      'Farming/agriculture activities',
      'Real estate development'
    ],
    benefits: [
      {
        category: 'General Category',
        items: [
          'Urban Areas: 15% subsidy',
          'Rural Areas: 25% subsidy',
          'Beneficiary contribution: 10%'
        ]
      },
      {
        category: 'Special Category (SC/ST/OBC/Women/Minorities/Ex-Servicemen/PH/Transgender/NER/Hill/Border)',
        items: [
          'Urban Areas: 25% subsidy',
          'Rural Areas: 35% subsidy',
          'Beneficiary contribution: 5%'
        ]
      },
      {
        category: 'Project Limits',
        items: [
          'Manufacturing sector: Up to ₹50 Lakh',
          'Business/Service sector: Up to ₹20 Lakh',
          'Per capita investment: ₹1 Lakh (plain), ₹1.5 Lakh (hilly)',
          'Bank loan covers ~90% of project cost'
        ]
      }
    ],
    process: [
      'Online application via www.kviconline.gov.in/pmegpeportal',
      'Implemented by KVIC/KVIB (rural), DICs (urban)',
      'EDP training mandatory via www.udyami.org.in',
      'Subsidy credited post 3-year lock-in period'
    ]
  },
  'vyupy': {
    name: 'VYUPY',
    fullName: 'Vishwakarma Yuva Udyami Protsahan Yojana',
    icon: <Scale className="w-10 h-10" />,
    color: 'from-orange-500 to-orange-600',
    validTill: 'March 31, 2029',
    tagline: 'Flagship scheme for youth entrepreneurship in Rajasthan',
    overview: 'VYUPY is a flagship initiative of the Rajasthan Government to promote youth entrepreneurship by providing low-cost institutional finance with interest subsidy up to 8% and margin money support up to ₹5 Lakh. The scheme targets individuals aged 18-45 years.',
    highlights: [
      { icon: <Percent />, title: 'Interest Subsidy', value: 'Up to 8%' },
      { icon: <IndianRupee />, title: 'Margin Money', value: '25% or ₹5 Lakh' },
      { icon: <Building2 />, title: 'Max Project', value: '₹2 Crore' },
      { icon: <Users />, title: 'Age Group', value: '18-45 Years' }
    ],
    eligibility: [
      'Individuals aged 18 to 45 years',
      'Proprietorships, partnerships, LLPs, companies, cooperatives',
      'For firms/companies: 51%+ ownership by eligible age group',
      'MSMEs as per Government of India definitions'
    ],
    ineligible: [
      'Units with capital/interest subsidy in last 5 years',
      'Bank defaulters or wilful defaulters',
      'Liquor, intoxicants, explosives',
      'Commercial transport vehicles above ₹15 Lakh',
      'Agriculture/allied, mining, real estate',
      'Activities by NGOs/trusts'
    ],
    benefits: [
      {
        category: 'Interest Subsidy',
        items: [
          'Up to ₹1 Crore loan: 8% interest subsidy',
          '₹1 Crore - ₹2 Crore loan: 7% interest subsidy',
          'Duration: 5 years',
          'Additional 1% for women, SC/ST, PwBD, rural enterprises'
        ]
      },
      {
        category: 'Margin Money Subsidy',
        items: [
          '25% of loan amount or ₹5 Lakh (whichever is lower)',
          'Paid directly to lending institution',
          'No interest on margin money component',
          'Adjustable after 3 years (if account not NPA)'
        ]
      },
      {
        category: 'Loan Terms',
        items: [
          'Maximum project cost: ₹2 Crore',
          'Land & Building: Up to 25% of loan',
          'Minimum promoter contribution: 10%',
          'Working capital: Max 30% in composite loans',
          'Loan tenure: Up to 7 years',
          'Moratorium: Up to 6 months'
        ]
      }
    ],
    process: [
      'Application via District Level Task Force Committee (DLTFC)',
      'Online application portal available',
      'Banks require DLTFC approval for sanction',
      'Loans may be linked with CGTMSE'
    ]
  },
  'msme-policy-2024': {
    name: 'MSME Policy 2024',
    fullName: 'Rajasthan MSME Policy 2024 – Key Incentive Schemes',
    icon: <Boxes className="w-10 h-10" />,
    color: 'from-teal-500 to-teal-600',
    validTill: 'March 31, 2029',
    tagline: 'Comprehensive support to reduce capital cost and improve MSME competitiveness',
    overview: 'The Rajasthan MSME Policy 2024 provides a structured framework of incentives including additional interest subsidy, CGTMSE fee reimbursement, SME exchange listing support, technology acquisition, quality enhancement, and digitization assistance.',
    highlights: [
      { icon: <Percent />, title: 'Additional Interest', value: 'Up to 2%' },
      { icon: <IndianRupee />, title: 'SME Listing', value: '₹15 Lakh' },
      { icon: <Award />, title: 'Tech Acquisition', value: '50% / ₹5 Lakh' },
      { icon: <Shield />, title: 'CGTMSE Fee', value: '100% for 7 Years' }
    ],
    eligibility: [
      'Micro, Small, and Medium Enterprises in Rajasthan',
      'MSMEs already approved under RIPS 2024 (for additional interest)',
      'Enterprises using CGTMSE loans up to ₹5 Cr',
      'ODOP, SC/ST/Women-owned, young entrepreneurs (18-45)',
      'Units in Area Category 3'
    ],
    benefits: [
      {
        category: 'Additional Interest Subsidy',
        items: [
          'Up to ₹5 Cr loans: Additional 2%',
          '₹5-10 Cr loans: Additional 1%',
          '₹10-50 Cr loans: Additional 0.5%',
          'Over and above RIPS 2024 benefits'
        ]
      },
      {
        category: 'CGTMSE Support',
        items: [
          '100% reimbursement of Annual Guarantee Fees',
          'For collateral-free loans up to ₹5 Cr',
          'Duration: 7 years',
          'Eligible: ODOP, SC/ST/Women, Young entrepreneurs'
        ]
      },
      {
        category: 'SME Exchange Listing',
        items: [
          'One-time reimbursement up to ₹15 Lakhs',
          'For Small & Medium Enterprises',
          'Successfully listed on SME platforms'
        ]
      },
      {
        category: 'Technology & Quality',
        items: [
          'Technology Acquisition: 50% reimbursement, max ₹5 Lakhs',
          'Quality Enhancement: 50% reimbursement, max ₹3 Lakhs',
          'Covers BIS, FSSAI, ISO, ZED certifications',
          'IPR registration support included'
        ]
      },
      {
        category: 'Market & Digital',
        items: [
          'Trade fair participation support',
          'Business Digitization: 75%, max ₹50,000',
          'eCommerce Platform Fees: 75%, max ₹50,000'
        ]
      }
    ]
  },
  'export-policy-2024': {
    name: 'Export Policy 2024',
    fullName: 'Rajasthan Export Promotion Policy 2024',
    icon: <Ship className="w-10 h-10" />,
    color: 'from-indigo-500 to-indigo-600',
    validTill: 'March 31, 2029',
    tagline: 'Boost exports with logistics support, compliance assistance, and market access',
    overview: 'The Rajasthan Export Promotion Policy 2024 is designed to boost exports, improve global market access, strengthen export-oriented ecosystem, and support MSMEs & emerging exporters through various reimbursements and incentives.',
    highlights: [
      { icon: <Percent />, title: 'Trade Fair', value: 'Up to 75%' },
      { icon: <IndianRupee />, title: 'Tech Support', value: 'Up to 75%' },
      { icon: <Award />, title: 'Testing', value: '75% Reimbursement' },
      { icon: <Ship />, title: 'Freight Subsidy', value: 'Logistics Support' }
    ],
    eligibility: [
      'Manufacturer Exporters',
      'Service-Oriented Exporters',
      'Merchant Exporters',
      'Member of Rajasthan Export Promotion Council (REPC)',
      'Valid IEC Code required',
      'Operating unit in Rajasthan'
    ],
    benefits: [
      {
        category: 'Compliance & Certification',
        items: [
          'Export Compliance Costs: Up to 50% reimbursement',
          'Covers quality testing, ISO/BIS certifications',
          'Export documentation support',
          'Product Testing: Up to 75% reimbursement'
        ]
      },
      {
        category: 'Marketing Support',
        items: [
          'Trade Fairs/Exhibitions: Up to 75% reimbursement',
          'Covers stall, travel, freight (subject to caps)',
          'International and domestic events',
          'State Export Awards recognition'
        ]
      },
      {
        category: 'Technology & Logistics',
        items: [
          'Technology Acquisition: Up to 75% assistance',
          'For productivity, environment, energy efficiency',
          'Freight Subsidy on inland transportation',
          'Special provisions for MSMEs, first-time exporters'
        ]
      },
      {
        category: 'Digital Export',
        items: [
          'eCommerce platform fee reimbursement',
          'Subscription/referral/logistics fees covered',
          'Export Credit Insurance: Up to 50% ECGC premium'
        ]
      }
    ],
    process: [
      'REPC designated as implementation agency',
      'DICCs involved in execution',
      'Online application and tracking',
      'Document verification required'
    ]
  },
  'logistics-policy-2025': {
    name: 'Logistics Policy 2025',
    fullName: 'Rajasthan Logistics Policy 2025',
    icon: <Factory className="w-10 h-10" />,
    color: 'from-cyan-500 to-cyan-600',
    validTill: 'March 31, 2029',
    tagline: 'Infrastructure development for warehouses, cold storage, and logistics parks',
    overview: 'The Rajasthan Logistics Policy 2025 establishes a comprehensive framework to strengthen logistics infrastructure, enhance multimodal connectivity, improve operational efficiency, and attract private investment with significant capital and interest subsidies.',
    highlights: [
      { icon: <Percent />, title: 'Capital Subsidy', value: '25% of EFCI' },
      { icon: <TrendingUp />, title: 'Interest Subsidy', value: '7% for 7 Years' },
      { icon: <Shield />, title: 'Electricity Duty', value: '100% Exempt' },
      { icon: <Award />, title: 'Green Incentives', value: 'Up to 50%' }
    ],
    eligibility: [
      'Proprietorships, Partnership Firms, LLPs, Companies',
      'Registered Societies, Joint Ventures/Consortiums',
      'Approved Investment Funds (Domestic/Foreign)',
      'Financial and technical eligibility norms apply'
    ],
    benefits: [
      {
        category: 'Financial Incentives (via RIPS 2024)',
        items: [
          '100% Electricity Duty Exemption (7 years)',
          '100% Mandi/Market Fee Reimbursement (7 years)',
          'Stamp Duty Relief',
          'Land Conversion Charge Relief'
        ]
      },
      {
        category: 'Capital/Interest Subsidy',
        items: [
          'Capital Subsidy: 25% of EFCI (distributed annually)',
          'Interest Subsidy: 7% for 7 years (annual caps apply)',
          'Choose either Capital OR Interest Subsidy',
          'Segment-specific ceilings apply'
        ]
      },
      {
        category: 'Technology Incentives',
        items: [
          'Vehicle tracking devices reimbursement',
          'Logistics Management Software support',
          'Fire Detection Systems assistance'
        ]
      },
      {
        category: 'Green Logistics',
        items: [
          'Up to 50% reimbursement (subject to caps)',
          'Effluent Treatment Systems',
          'Waste Management Infrastructure',
          'Renewable Energy Solutions',
          'Green Building Certifications'
        ]
      },
      {
        category: 'Non-Financial Benefits',
        items: [
          'Land reservation in RIICO industrial areas',
          '24×7 operations permitted',
          'Ground Coverage up to 60%',
          'Formal Industry Status for logistics'
        ]
      }
    ],
    infrastructure: [
      'Warehouses', 'Silos', 'Cold Storage Facilities',
      'Inland Container Depots (ICDs)', 'Container Freight Stations',
      'Air Freight Stations', 'Cargo Terminals',
      'Multi-Modal Logistics Parks (MMLPs)', 'Integrated Logistics Parks'
    ]
  },
  'odop-policy-2024': {
    name: 'ODOP Policy 2024',
    fullName: 'Rajasthan One District One Product Policy 2024',
    icon: <MapPin className="w-10 h-10" />,
    color: 'from-rose-500 to-rose-600',
    validTill: 'March 31, 2029',
    tagline: 'District-specific support for flagship products with comprehensive assistance',
    overview: 'The ODOP Policy 2024 is a strategic initiative to strengthen district-specific industries by supporting enterprises aligned with identified flagship products. It improves access to finance, technology, quality enhancement, market access, and digital expansion.',
    highlights: [
      { icon: <IndianRupee />, title: 'Margin Money', value: 'Up to ₹20 Lakh' },
      { icon: <Award />, title: 'Technology', value: '50% / ₹5 Lakh' },
      { icon: <Shield />, title: 'Quality Cert', value: '75% / ₹3 Lakh' },
      { icon: <TrendingUp />, title: 'Marketing', value: 'Up to ₹2 Lakh' }
    ],
    eligibility: [
      'Enterprises aligned with district flagship products',
      'Udyam Registration mandatory for Margin Money',
      'Must demonstrate performance improvement (certified by CE) for tech support',
      'Expanding into online sales for eCommerce assistance'
    ],
    benefits: [
      {
        category: 'Margin Money Assistance',
        items: [
          'Micro Enterprises: 25% of project cost, max ₹15 Lakhs',
          'Small Enterprises: 15% of project cost, max ₹20 Lakhs',
          'Additional ₹5 Lakhs for SC/ST, Women, PwBD, Young (<35 yrs)',
          'Building cost capped at 25%, working capital at 10%'
        ]
      },
      {
        category: 'Technology Support',
        items: [
          '50% reimbursement, max ₹5 Lakhs per unit',
          'Advanced machinery acquisition',
          'Production technologies',
          'Design, drawings, software, automation',
          'Must show measurable performance improvement'
        ]
      },
      {
        category: 'Quality & Certification',
        items: [
          '75% reimbursement, max ₹3 Lakhs',
          'BIS/FSSAI, ISO/ZED certifications',
          'Product & system certifications',
          'IPR including testing & documentation'
        ]
      },
      {
        category: 'Marketing Assistance',
        items: [
          'Within Rajasthan: ₹50,000/event, max 3 events/year',
          'Across India: ₹1.5 Lakhs/event, max 2 events/year',
          'International: ₹2 Lakhs/event, max 1 event/year',
          'Travel reimbursements included'
        ]
      },
      {
        category: 'Digital Expansion',
        items: [
          'eCommerce Platform Fees: 75%, up to ₹1 Lakh/year (2 years)',
          'Cataloguing/Website: 60%, max ₹75,000 (one-time)'
        ]
      }
    ]
  }
};

const SubsidyDetail = () => {
  const { schemeId } = useParams();
  const scheme = subsidyData[schemeId];

  if (!scheme) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-heading font-semibold text-[#0f172a] mb-4">Scheme Not Found</h1>
          <Link to="/subsidies" className="text-[#3b5998] hover:text-[#00bcd4]">
            ← Back to Subsidies
          </Link>
        </div>
      </div>
    );
  }

  const schemeIds = Object.keys(subsidyData);
  const currentIndex = schemeIds.indexOf(schemeId);
  const prevScheme = currentIndex > 0 ? schemeIds[currentIndex - 1] : null;
  const nextScheme = currentIndex < schemeIds.length - 1 ? schemeIds[currentIndex + 1] : null;

  return (
    <div data-testid={`subsidy-${schemeId}`} className="pt-20">
      <SubsidyHero scheme={scheme} schemeId={schemeId} />
      <SubsidyHighlights scheme={scheme} />
      <SubsidyOverview scheme={scheme} />
      <SubsidyEligibility scheme={scheme} />
      <SubsidyBenefits scheme={scheme} />
      <SubsidyExtras scheme={scheme} />
      <SubsidySchemeCTA scheme={scheme} />
      <SubsidyNavigation prevScheme={prevScheme} nextScheme={nextScheme} subsidyData={subsidyData} />
    </div>
  );
};

export default SubsidyDetail;
